import React, { useState, useEffect, useCallback, useRef } from 'react';
import './FilterModal.css';
import SearchableDropdown from './SearchableDropdown';
import { equipmentService } from '../features/equipment/services/equipmentService';
import { IoMdCloseCircle } from 'react-icons/io';

const EquipmentFilterModal = ({ isOpen, onClose, onApplyFilters, onClearFilters, initialFilters }) => {
  const [isApplying, setIsApplying] = useState(false);
  const [filters, setFilters] = useState({
    productId: '',
    productName: '',
    makeId: '',
    makeName: '',
    modelId: '',
    modelName: ''
  });

  // State for dropdown options
  const [products, setProducts] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  // Use refs to track ongoing searches and debounce timers
  const productSearchTimeout = useRef(null);
  const makeSearchTimeout = useRef(null);
  const modelSearchTimeout = useRef(null);

  useEffect(() => {
    if (initialFilters && isOpen) {
      setFilters(initialFilters);
    }
  }, [initialFilters, isOpen]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (productSearchTimeout.current) clearTimeout(productSearchTimeout.current);
      if (makeSearchTimeout.current) clearTimeout(makeSearchTimeout.current);
      if (modelSearchTimeout.current) clearTimeout(modelSearchTimeout.current);
    };
  }, []);

  const loadProducts = useCallback(async (searchQuery = '') => {
    setLoadingProducts(true);
    try {
      const response = await equipmentService.searchProducts(searchQuery);
      if (response.success && Array.isArray(response.data)) {
        const formattedProducts = response.data.map(item => ({
          id: item.id || item.product_id,
          name: item.name || item.product_name
        }));
        setProducts(formattedProducts);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const loadMakes = useCallback(async (productId, searchQuery = '') => {
    setLoadingMakes(true);
    try {
      const response = await equipmentService.searchMakes(searchQuery, productId);
      if (response.success && Array.isArray(response.data)) {
        const formattedMakes = response.data.map(item => ({
          id: item.id || item.make_id,
          name: item.name || item.make_name
        }));
        setMakes(formattedMakes);
      }
    } catch (error) {
      console.error('Error loading makes:', error);
      setMakes([]);
    } finally {
      setLoadingMakes(false);
    }
  }, []);

  const loadModels = useCallback(async (makeId, searchQuery = '', productId) => {
    setLoadingModels(true);
    try {
      const response = await equipmentService.searchModels(searchQuery, makeId, productId);
      if (response.success && Array.isArray(response.data)) {
        const formattedModels = response.data.map(item => ({
          id: item.id || item.model_id,
          name: item.name || item.model_name
        }));
        setModels(formattedModels);
      }
    } catch (error) {
      console.error('Error loading models:', error);
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  }, []);

  const handleProductSelect = useCallback((product) => {
    setFilters(prev => ({
      ...prev,
      productId: product.id,
      productName: product.name,
      makeId: '',
      makeName: '',
      modelId: '',
      modelName: ''
    }));
    setMakes([]);
    setModels([]);
  }, []);

  const handleMakeSelect = useCallback((make) => {
    setFilters(prev => ({
      ...prev,
      makeId: make.id,
      makeName: make.name,
      modelId: '',
      modelName: ''
    }));
    setModels([]);
  }, []);

  const handleModelSelect = useCallback((model) => {
    setFilters(prev => ({
      ...prev,
      modelId: model.id,
      modelName: model.name
    }));
  }, []);

  // These handlers ONLY update the input value, NOT trigger API calls
  const handleProductChange = useCallback((searchValue) => {
    setFilters(prev => ({ ...prev, productName: searchValue, productId: '' }));
    
    // Debounce API call
    if (productSearchTimeout.current) {
      clearTimeout(productSearchTimeout.current);
    }
    productSearchTimeout.current = setTimeout(() => {
      loadProducts(searchValue);
    }, 300);
  }, [loadProducts]);

  const handleMakeChange = useCallback((searchValue) => {
    setFilters(prev => ({ ...prev, makeName: searchValue, makeId: '' }));
    
    // Debounce API call
    if (makeSearchTimeout.current) {
      clearTimeout(makeSearchTimeout.current);
    }
    // if (filters.productId) {
      makeSearchTimeout.current = setTimeout(() => {
        loadMakes(filters.productId, searchValue);
      }, 300);
    // }
  }, [filters.productId, loadMakes]);

  const handleModelChange = useCallback((searchValue) => {
    setFilters(prev => ({ ...prev, modelName: searchValue, modelId: '' }));
    
    // Debounce API call
    if (modelSearchTimeout.current) {
      clearTimeout(modelSearchTimeout.current);
    }
    // if (filters.makeId && filters.productId) {
      modelSearchTimeout.current = setTimeout(() => {
        loadModels(filters.makeId, searchValue, filters.productId);
      }, 300);
    // }
  }, [filters.makeId, filters.productId, loadModels]);

  const handleProductFocus = useCallback(() => {
    if (products.length === 0 && !loadingProducts) {
      loadProducts('');
    }
  }, [products.length, loadingProducts, loadProducts]);

  const handleMakeFocus = useCallback(() => {
    if (makes.length === 0 && !loadingMakes) {
      loadMakes(filters.productId, '');
    }
  }, [filters.productId, makes.length, loadingMakes, loadMakes]);

  const handleModelFocus = useCallback(() => {
    if ( models.length === 0 && !loadingModels) {
      loadModels(filters.makeId, '', filters.productId);
    }
  }, [filters.makeId, filters.productId, models.length, loadingModels, loadModels]);

  const handleClearAll = () => {
    setFilters({
      productId: '',
      productName: '',
      makeId: '',
      makeName: '',
      modelId: '',
      modelName: ''
    });
    setProducts([]);
    setMakes([]);
    setModels([]);
    onClearFilters();
    onClose();
  };

  const handleProductClear = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      productId: '',
      productName: '',
      makeId: '',
      makeName: '',
      modelId: '',
      modelName: ''
    }));
    setMakes([]);
    setModels([]);
  }, []);

  const handleMakeClear = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      makeId: '',
      makeName: '',
      modelId: '',
      modelName: ''
    }));
    setModels([]);
  }, []);

  const handleModelClear = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      modelId: '',
      modelName: ''
    }));
  }, []);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      await onApplyFilters(filters);
      onClose();
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setIsApplying(false);
    }
  };

  const hasActiveFilters = () => {
    return filters.productId || filters.makeId || filters.modelId;
  };

  if (!isOpen) return null;

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div 
        className="filter-modal-container" 
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '600px' }}
      >
        {/* Header */}
        <div className="filter-modal-header">
          <h2 className="filter-modal-title">Filters</h2>
          <div className="filter-modal-actions">
            <button 
              className="filter-clear-button"
              onClick={handleClearAll}
              disabled={!hasActiveFilters()}
            >
              Clear Filter
            </button>
            <button 
              className="filter-apply-button"
              onClick={handleApply}
              disabled={isApplying}
            >
              {isApplying ? 'Applying...' : 'Apply Filter'}
            </button>
          </div>
           <IoMdCloseCircle  onClick={onClose} size={18} color='brown' className='drawer_close_icon' />
        </div>

        {/* Content */}
        <div className="filter-modal-content" style={{ minHeight: '200px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SearchableDropdown
              value={filters.productName}
              onChange={handleProductChange}
              onSelect={handleProductSelect}
              onClear={handleProductClear}
              onFocus={handleProductFocus}
              placeholder="Type to search product..."
              options={products}
              label="Search Product"
              disabled={false}
              loading={loadingProducts}
              disableLocalFilter={true}
           className="filter-input"
            />
            
            <SearchableDropdown
              value={filters.makeName}
              onChange={handleMakeChange}
              onSelect={handleMakeSelect}
              onClear={handleMakeClear}
              onFocus={handleMakeFocus}
              placeholder={
            "Type to search make..."
              }
              options={makes}
              label="Search Make"
              loading={loadingMakes}
              disableLocalFilter={true}
              className="filter-input"
            />
            
            <SearchableDropdown
              value={filters.modelName}
              onChange={handleModelChange}
              onSelect={handleModelSelect}
              onClear={handleModelClear}
              onFocus={handleModelFocus}
              placeholder={
                "Type to search model..."
              }
              options={models}
              label="Search Model"
              loading={loadingModels}
              disableLocalFilter={true}
              className="filter-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentFilterModal;